import { useCallback, useEffect, useRef, useState } from 'react'
import { getConfigValue } from '~/helpers/envHelpers'

import { SignalingContext, SignalingMessage, SignalingStatus } from './context'

type JobHandler = (message: SignalingMessage) => void

const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_DELAY_MS = 2000
const PING_INTERVAL_MS = 30000

export function SignalingProvider({ children }: { children: React.ReactNode }) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isUnmountedRef = useRef(false)
  const jobHandlersRef = useRef(new Map<string, Set<JobHandler>>())
  const [status, setStatus] = useState<SignalingStatus>('connecting')

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const send = useCallback((message: Record<string, unknown>) => {
    const socket = wsRef.current

    if (socket?.readyState !== WebSocket.OPEN) {
      return false
    }

    socket.send(JSON.stringify(message))
    return true
  }, [])

  const replaySubscriptions = useCallback(() => {
    const jobIds = [...jobHandlersRef.current.keys()]

    if (jobIds.length === 0) {
      return
    }

    send({
      type: 'subscribe',
      topics: jobIds,
    })
  }, [send])

  useEffect(() => {
    isUnmountedRef.current = false

    function connect() {
      clearTimers()
      const socket = new WebSocket(getConfigValue('VITE_TRPC_WS_URL'))
      wsRef.current = socket
      setStatus('connecting')

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0
        setStatus('open')
        replaySubscriptions()

        pingIntervalRef.current = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }))
          }
        }, PING_INTERVAL_MS)
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data) as SignalingMessage

        if (!data.jobId) {
          return
        }

        const handlers = jobHandlersRef.current.get(data.jobId)
        if (!handlers) {
          return
        }

        handlers.forEach((handler) => handler(data))
      }

      socket.onclose = () => {
        clearTimers()

        if (isUnmountedRef.current) {
          return
        }

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setStatus('closed')
          return
        }

        setStatus('connecting')
        reconnectAttemptsRef.current += 1
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS)
      }

      socket.onerror = () => {
        socket.close()
      }
    }

    connect()

    return () => {
      isUnmountedRef.current = true
      clearTimers()
      wsRef.current?.close()
    }
  }, [clearTimers, replaySubscriptions])

  const subscribeToJob = useCallback((jobId: string, handler: JobHandler) => {
    const handlers = jobHandlersRef.current.get(jobId) ?? new Set<JobHandler>()
    const isNewJob = !jobHandlersRef.current.has(jobId)

    handlers.add(handler)
    jobHandlersRef.current.set(jobId, handlers)

    if (isNewJob && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'subscribe',
          topics: [jobId],
        }),
      )
    }

    return () => {
      const currentHandlers = jobHandlersRef.current.get(jobId)
      if (!currentHandlers) {
        return
      }

      currentHandlers.delete(handler)

      if (currentHandlers.size > 0) {
        return
      }

      jobHandlersRef.current.delete(jobId)

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'unsubscribe',
            topics: [jobId],
          }),
        )
      }
    }
  }, [])

  return (
    <SignalingContext.Provider value={{ status, send, subscribeToJob }}>
      {children}
    </SignalingContext.Provider>
  )
}
